<script setup lang="ts">
import { ref, watch } from 'vue';
import fileUpload from '@/shared/upload';

const { beforeUpload } = fileUpload('stickers', 'Sticker');

const props = defineProps({
  url: String,
  onAddImage: Function,
});

const localPreviewUrl = ref<string | null>(null);
const isUploading = ref(false);

watch(
  () => props.url,
  (newUrl) => {
    if (newUrl) {
      if (localPreviewUrl.value) {
        URL.revokeObjectURL(localPreviewUrl.value);
        localPreviewUrl.value = null;
      }
      isUploading.value = false;
    }
  },
);

const handleHttpRequest = async (payload: any) => {
  if (payload.file) {
    localPreviewUrl.value = URL.createObjectURL(payload.file);
  }
  isUploading.value = true;
  try {
    await props.onAddImage?.(payload);
  } finally {
    isUploading.value = false;
  }
};
</script>

<template lang="pug">
section.c-sticker-content
  el-upload(
    drag
    :show-file-list="false"
    :before-upload="beforeUpload"
    :http-request="handleHttpRequest"
    action=""
  )
    .c-sticker-content__preview(v-if="localPreviewUrl || props.url")
      img(:src="localPreviewUrl || props.url" class="img-fluid")
      transition(name="fade")
        .c-sticker-content__loading-overlay(v-if="isUploading")
          .c-sticker-content__spinner
          span.c-sticker-content__loading-text Uploading...

    template(v-else)
      fai(type="fas" icon="file-image" height="90px" width="60px" color="#B2B2B2")
      p.c-sticker-content__drop-text Drop an image file here or
      button.c-sticker-content__btn-upload
        fai(type="fas" icon="images" width="16px" height="14px")
        | Browse For File
</template>

<style lang="scss">
.c-sticker-content {
  .el-upload {
    .el-upload-dragger {
      height: 433px;
      width: 335px;
      padding: 30px;
      border: 1px solid #ccc !important;
      border-radius: 5px;
    }
  }

  &__preview {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 4px;
    }
  }

  &__loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border-radius: 4px;
  }

  &__spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: sticker-spin 0.75s linear infinite;
  }

  &__loading-text {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  &__drop-text {
    font-weight: 700;
    font-size: 16px;
    color: #666666;
    margin: 30px 0;
  }

  &__btn-upload {
    background: #333333;
    border-radius: 56px;
    border: none;
    width: 100%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
    gap: 10px;
    min-height: 48px;
  }
}

@keyframes sticker-spin {
  to { transform: rotate(360deg); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
